import { useState } from "react";
import { Alert, Button, Col, Form, Input, Row, Select, Switch, Tag, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../core/service/ApiService";

/**
 * Uygulama ayarlarının bir grubunu (Genel / Mail / SMS / HL7 / RIS) düzenleyen sekme.
 *
 * Alanlar ŞEMADAN üretilir (backend: ConfigSchemaGenerator + ConfigDoc öznitelikleri). Yani yeni bir
 * ayar eklendiğinde bu ekrana elle alan eklemek gerekmez; açıklaması, tipi, seçenekleri ve sır olup
 * olmadığı POCO'dan gelir. Tek kaynak: ServiceConfiguration.
 *
 * Kurallar:
 *   - Sır alanları (Secret) sunucudan DEĞER olarak gelmez. Boş bırakılırsa mevcut değer korunur.
 *   - Yol alanları (PathKind): dosya için dosya, dizin için ZIP yüklenebilir — yalnız Uploadable ise.
 *   - RestartRequired alanlar işaretlenir: kaydetmek yeterli değil, uygulama yeniden başlamalı.
 */
export default function AppConfigTab({ nodes, onSaved })
{
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [restartKeys, setRestartKeys] = useState([]);

    // form alan adında nokta olamaz (antd iç içe yol sanar) → "Mail.Password" ↔ "Mail__Password"
    const toField = (key) => key.replace(/\./g, "__");
    const toKey = (field) => field.replace(/__/g, ".");

    const initialValues = {};
    nodes.forEach((node) => {
        initialValues[toField(node.key)] = node.secret ? undefined : node.value;
    });

    const save = async (values) => {
        setLoading(true);

        const payload = {};
        Object.entries(values).forEach(([field, value]) => {
            const key = toKey(field);
            const node = nodes.find((n) => n.key === key);
            if (!node) return;

            if (node.editable === false) return;   // salt okunur → gönderilmez (sunucu da reddeder)

            // sır: boş bırakıldıysa gönderme (mevcut değer korunur)
            if (node.secret && (value === undefined || value === null || value === "")) return;

            payload[key] = value;
        });

        const res = await ApiService.AppConfig.Save(payload);
        setLoading(false);

        if (res?.success) {
            setRestartKeys(res.data?.restartRequired || []);
            onSaved?.();
        }
    };

    const upload = async (node, file) => {
        const res = await ApiService.AppConfig.UploadPath(node.key, file);
        if (res?.success) {
            message.success(t("Uploaded. The application must be restarted for it to take effect."));
            onSaved?.();
        }
        return false;   // antd Upload'un kendi isteğini engelle
    };

    const renderControl = (node) => {
        // Editable=false (ör. JwtKey): salt okunur gösterilir. Sunucu da reddediyor — arayüzde
        // gizlemek tek başına güvenlik değildir, ikisi birlikte.
        const disabled = node.editable === false;

        if (node.type === "bool") {
            return <Switch disabled={disabled} />;
        }

        if (node.allowedValues?.length) {
            return (
                <Select
                    allowClear
                    disabled={disabled}
                    options={node.allowedValues.map((v) => ({ value: v, label: String(v) }))}
                />
            );
        }

        if (node.secret) {
            return disabled
                ? <Input value="••••••••" disabled />
                : <Input.Password placeholder={t("Leave empty to keep the current value")} autoComplete="new-password" />;
        }

        return <Input disabled={disabled} />;
    };

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={save}
        >
            {restartKeys.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={t("Saved. These settings take effect only after the application restarts:")}
                    description={restartKeys.join(", ")}
                />
            )}

            <Row gutter={16}>
                {nodes.map((node) => (
                    <Col xs={24} md={12} key={node.key}>
                        <Form.Item
                            name={toField(node.key)}
                            valuePropName={node.type === "bool" ? "checked" : "value"}
                            label={
                                <span className="config-label">
                                    {node.name}
                                    {node.restartRequired && (
                                        <Tag color="warning" className="config-tag">{t("restart required")}</Tag>
                                    )}
                                    {node.secret && (
                                        <Tag className="config-tag">{t("secret")}</Tag>
                                    )}
                                    {node.editable === false && (
                                        <Tag color="default" className="config-tag">{t("read only")}</Tag>
                                    )}
                                </span>
                            }
                            extra={node.description}
                        >
                            {renderControl(node)}
                        </Form.Item>

                        {/* yol ayarı: içeriği yüklenebilir mi? (dizin → ZIP, dosya → dosya) */}
                        {node.uploadable && (
                            <div className="config-upload">
                                <Upload
                                    accept={node.pathKind === "Directory" ? ".zip" : undefined}
                                    showUploadList={false}
                                    beforeUpload={(file) => upload(node, file)}
                                >
                                    <Button icon={<UploadOutlined />}>
                                        {node.pathKind === "Directory" ? t("Upload ZIP") : t("Upload file")}
                                    </Button>
                                </Upload>
                                <span className="config-upload-hint">
                                    {node.pathKind === "Directory"
                                        ? t("The ZIP is extracted into this directory, replacing its contents.")
                                        : t("The file is written to this path.")}
                                </span>
                            </div>
                        )}
                    </Col>
                ))}
            </Row>

            <Button type="primary" htmlType="submit" loading={loading}>
                {t("Save")}
            </Button>
        </Form>
    );
}
